/**
 * Toolbar and sortable header for a list screen.
 *
 * One toolbar, used identically everywhere, so the search box is always in the same
 * place and the sort control always behaves the same way. A portal where every screen
 * invents its own filter row is a portal people have to relearn per screen.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import { useI18n, type TranslationKey } from '@/i18n';
import type { SortOption, TableState, ViewMode } from '@/lib/table';

/** Magnifying glass, rendered inline so the toolbar needs no icon dependency. */
function SearchIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className="w-4 h-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <circle cx="9" cy="9" r="5.5" />
      <path d="M13.2 13.2 17 17" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className="w-3.5 h-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="m6 6 8 8M14 6l-8 8" />
    </svg>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  autoFocus = false,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLInputElement>(null);

  // "/" focuses the search box, the shortcut every list UI has trained people to expect.
  // Ignored while typing somewhere else, so it never eats a slash in a real field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      e.preventDefault();
      ref.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <span className="absolute start-3 top-1/2 -translate-y-1/2 text-subtle pointer-events-none">
        <SearchIcon />
      </span>

      <input
        ref={ref}
        type="search"
        role="searchbox"
        autoFocus={autoFocus}
        className="input ps-9 pe-9"
        value={value}
        placeholder={placeholder ?? t('table.searchPlaceholder')}
        aria-label={t('table.search')}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Escape clears rather than blurs: with an empty box, blurring is what the
          // user wanted anyway, and with a full one clearing is always the intent.
          if (e.key === 'Escape') {
            if (value) {
              e.preventDefault();
              onChange('');
            } else {
              ref.current?.blur();
            }
          }
        }}
      />

      {value ? (
        <button
          type="button"
          onClick={() => {
            onChange('');
            ref.current?.focus();
          }}
          aria-label={t('common.clearFilters')}
          className="absolute end-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-subtle hover:text-text hover:bg-surface-alt transition-colors"
        >
          <ClearIcon />
        </button>
      ) : null}
    </div>
  );
}

/** Two-state segmented control. Used for view mode and for group-by. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  ariaLabel?: string;
}) {
  return (
    /* Scrolls rather than clips. Five status filters do not fit a phone's width, and a
       button half off the right edge reads as a rendering fault rather than as "there
       is more this way". */
    <div
      className="flex max-sm:overflow-x-auto max-sm:max-w-full"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option, i) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={`h-9 px-3 text-sm font-semibold border transition-colors whitespace-nowrap shrink-0 ${
              i === 0 ? 'rounded-s' : ''
            } ${i === options.length - 1 ? 'rounded-e' : ''} ${i > 0 ? '-ms-px' : ''} ${
              active
                ? 'bg-primary text-[var(--color-on-primary)] border-primary relative z-10'
                : 'bg-surface text-muted border-border hover:bg-surface-alt hover:text-text'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Search + sort + view, plus a result count.
 *
 * `extra` takes the screen's own filters (a date range, a status select) so they sit on
 * the same row rather than in a second panel above it.
 */
export function DataToolbar<T>({
  state,
  searchPlaceholder,
  extra,
  showView = false,
  countOne,
  countMany,
}: {
  state: TableState<T>;
  searchPlaceholder?: string;
  extra?: ReactNode;
  showView?: boolean;
  countOne?: TranslationKey;
  countMany?: TranslationKey;
}) {
  const { t, plural } = useI18n();

  const sortLabel = (sort: SortOption<T>) => t(sort.labelKey as TranslationKey);

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <SearchInput
        value={state.query}
        onChange={state.setQuery}
        placeholder={searchPlaceholder}
        className="w-full sm:w-72"
      />

      {extra}

      {/* `ms-auto` only once there is room for it. On a phone it pushed the count
          and the sort select past the right edge. */}
      <div className="flex items-center gap-3 sm:ms-auto max-sm:w-full max-sm:flex-wrap">
        {/* The count is how someone knows a search actually matched something, and how
            they notice a filter is still on from five minutes ago. */}
        <span className="text-sm text-muted tabular whitespace-nowrap">
          {plural(state.rows.length, countOne ?? 'table.resultsOne', countMany ?? 'table.resultsMany', {
            count: state.rows.length,
          })}
          {state.isFiltered ? ` ${t('table.filteredFrom', { total: state.total })}` : ''}
        </span>

        {state.sorts.length > 0 ? (
          <label className="flex items-center gap-2">
            <span className="sr-only">{t('table.sortBy')}</span>
            <select
              className="input w-auto max-sm:flex-1"
              value={`${state.sortKey}:${state.direction}`}
              onChange={(e) => {
                const [key, dir] = e.target.value.split(':');
                state.setSort(key!, dir === 'desc' ? 'desc' : 'asc');
              }}
            >
              {state.sorts.map((sort) => (
                <optgroup key={sort.key} label={sortLabel(sort)}>
                  <option value={`${sort.key}:asc`}>
                    {sortLabel(sort)} · {t('table.ascending')}
                  </option>
                  <option value={`${sort.key}:desc`}>
                    {sortLabel(sort)} · {t('table.descending')}
                  </option>
                </optgroup>
              ))}
            </select>
          </label>
        ) : null}

        {showView ? (
          <Segmented<ViewMode>
            ariaLabel={t('table.view')}
            value={state.view}
            onChange={state.setView}
            options={[
              { value: 'table', label: t('table.viewTable') },
              { value: 'cards', label: t('table.viewCards') },
            ]}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * A sortable column header.
 *
 * The arrow is only rendered on the active column — an indicator on every column is
 * noise, and a greyed-out arrow reads as "disabled" rather than "sortable".
 */
export function SortableTh<T>({
  state,
  sortKey,
  children,
  className = '',
  align = 'left',
}: {
  state: TableState<T>;
  sortKey: string;
  children: ReactNode;
  className?: string;
  align?: 'left' | 'right';
}) {
  const { t } = useI18n();
  const active = state.sortKey === sortKey;
  const ascending = state.direction === 'asc';

  return (
    <th
      className={`th ${className}`}
      aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => state.toggleSort(sortKey)}
        title={t('table.sortBy')}
        className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-xs transition-colors hover:text-text ${
          active ? 'text-text' : ''
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {children}
        <span aria-hidden="true" className={active ? 'text-primary' : 'opacity-0'}>
          {ascending ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

/** Shown when a search or filter matched nothing — distinct from "there is no data". */
export function NoResults({ onClear }: { onClear: () => void }) {
  const { t } = useI18n();

  return (
    <div className="px-4 py-12 text-center">
      <p className="text-sm font-semibold">{t('common.noResults')}</p>
      <p className="text-sm text-muted mt-1">{t('common.noResultsHint')}</p>
      <button type="button" className="btn-secondary mt-4" onClick={onClear}>
        {t('common.clearFilters')}
      </button>
    </div>
  );
}
